function isCoordinatePair(value: unknown): value is [number, number] {
  return Array.isArray(value)
    && value.length >= 2
    && typeof value[0] === 'number'
    && typeof value[1] === 'number';
}

export type ThreatShapeMode = 'polygon' | 'pin';

export function isPointGeometry(geometry: any): boolean {
  return geometry?.type === 'Point';
}

export function isPolygonGeometry(geometry: any): boolean {
  return geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';
}

export function geometryShapeMode(geometry: any): ThreatShapeMode {
  return isPointGeometry(geometry) ? 'pin' : 'polygon';
}

export function normalizeThreatGeometry(geometry: any): any {
  if (!geometry?.type || !geometry?.coordinates) {
    return geometry;
  }

  if (geometry.type === 'Point') {
    return geometry;
  }

  if (geometry.type === 'Polygon') {
    const coords = geometry.coordinates;
    if (Array.isArray(coords) && coords.length > 0 && isCoordinatePair(coords[0])) {
      return { ...geometry, coordinates: [coords] };
    }

    return geometry;
  }

  if (geometry.type === 'MultiPolygon') {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map((polygonCoords: any) => {
        if (Array.isArray(polygonCoords) && polygonCoords.length > 0 && isCoordinatePair(polygonCoords[0])) {
          return [polygonCoords];
        }

        return polygonCoords;
      }),
    };
  }

  return geometry;
}

export function getPolygonRingCoordinates(geometry: any): number[][] | null {
  const normalized = normalizeThreatGeometry(geometry);

  if (normalized?.type === 'Polygon') {
    return normalized.coordinates?.[0] ?? null;
  }

  if (normalized?.type === 'MultiPolygon') {
    return normalized.coordinates?.[0]?.[0] ?? null;
  }

  return null;
}

export function getPointCoordinates(geometry: any): [number, number] | null {
  const normalized = normalizeThreatGeometry(geometry);
  if (normalized?.type !== 'Point') {
    return null;
  }

  const coords = normalized.coordinates;
  if (!isCoordinatePair(coords)) {
    return null;
  }

  return [coords[0], coords[1]];
}

const EARTH_RADIUS_MILES = 3958.7613;

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Builds a GeoJSON Polygon approximating a circle for map display.
 */
export function circlePolygonFromPoint(lng: number, lat: number, radiusMiles: number, steps = 64): any {
  const coordinates: number[][] = [];
  const latRad = toRadians(lat);

  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const angularDistance = radiusMiles / EARTH_RADIUS_MILES;

    const lat2 = Math.asin(
      Math.sin(latRad) * Math.cos(angularDistance)
      + Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const lng2 = toRadians(lng) + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRad),
      Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat2)
    );

    coordinates.push([toDegrees(lng2), toDegrees(lat2)]);
  }

  return {
    type: 'Polygon',
    coordinates: [coordinates],
  };
}

export function displayGeometryForThreat(geometry: any, radiusMiles?: number | null): any {
  const normalized = normalizeThreatGeometry(geometry);
  if (isPointGeometry(normalized) && radiusMiles && radiusMiles > 0) {
    const point = getPointCoordinates(normalized);
    if (point) {
      return circlePolygonFromPoint(point[0], point[1], radiusMiles);
    }
  }

  return normalized;
}
